/** @jsx jsx */
import React, { useContext } from 'react';
import { jsx, Button, Box, Input, Flex } from 'theme-ui';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Builder } from '@builder.io/react';
import { getBrandFromPath } from '../../utilities/brandTheme';
import { RegionConfigContext } from '../../utilities/RegionConfigContext';

const EMAIL_SIGNUP_STYLES = {
  kelly: {
    popupBg: '#FAF7EF',
    popupGradient: 'linear-gradient(135deg, #FAF7EF 0%, #F5F1E8 100%)',
    popupBorder: 'rgba(203, 186, 176, 0.3)',
    accent: '#D7A8A0',
    accentRgba: 'rgba(215, 168, 160, 0.1)',
    accentRgba15: 'rgba(215, 168, 160, 0.15)',
    accentRgba3: 'rgba(215, 168, 160, 0.3)',
    inputBg: '#FFFBF7',
    placeholder: '#CBBAB0',
  },
  spruce: {
    popupBg: '#faf6f1',
    popupGradient: 'linear-gradient(135deg, #f0f4fb 0%, #faf6f1 100%)',
    popupBorder: 'rgba(60, 81, 157, 0.2)',
    accent: '#3c519d',
    accentRgba: 'rgba(60, 81, 157, 0.1)',
    accentRgba15: 'rgba(60, 81, 157, 0.15)',
    accentRgba3: 'rgba(60, 81, 157, 0.3)',
    inputBg: '#ffffff',
    placeholder: '#7abae2',
  },
};

const EmailSignup = ({ 
  buttonText = 'Shop now', 
  buttonVariant = 'primary', 
  popupTitle = 'Join the waitlist',
  popupSubtitle = "Thank you for your interest! We haven't launched yet, but we will hold your spot in line and notify you as soon as we do.",
  popupButtonText = 'Submit', 
  placeholderText = 'Enter your email address',
  pageVariant = '',
  apiEndpoint,
  redirectUrl = '',
  regionConfig: regionConfigProp,
  builderBlock 
}) => {
  const regionConfigFromContext = useContext(RegionConfigContext);
  const regionConfig = regionConfigProp ?? regionConfigFromContext;
  const effectiveApiEndpoint = apiEndpoint || regionConfig?.zapierEndpoint || '';
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [brand, setBrand] = useState('kelly');
  useEffect(() => {
    setBrand(getBrandFromPath());
  }, []);
  const styles = EMAIL_SIGNUP_STYLES[brand] || EMAIL_SIGNUP_STYLES.kelly;


  // Function to get URL query parameters
  const getUrlParams = () => {
    if (typeof window === 'undefined') return {};
    
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_content: urlParams.get('utm_content') || '',
    };
  };

  // Function to format current timestamp
  const getCurrentTimestamp = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleButtonClick = () => {
    // Send GTM event when button is clicked
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({event: "labs_email_clicked"});
    }
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setEmail('');
    setShowConfirmation(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClosePopup();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show confirmation message
    setShowConfirmation(true);
    
    // Send GTM event
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({event: "labs_email_submitted"});
    }
    
    // Prepare data for API request
    const urlParams = getUrlParams();
    const timestamp = getCurrentTimestamp();
    
    // Get page variant from prop, or try to get from global variable set by template
    let currentPageVariant = pageVariant || (typeof window !== 'undefined' ? window.__builderPageUrl : undefined);
    
    const requestData = {
      email: email,
      date: timestamp,
      utm_campaign: urlParams.utm_campaign,
      utm_source: urlParams.utm_source,
      utm_medium: urlParams.utm_medium,
      utm_content: urlParams.utm_content,
      page_variant: currentPageVariant,
    };
    
    // Make POST request if API endpoint is provided
    if (effectiveApiEndpoint) {
      try {
        const response = await fetch(effectiveApiEndpoint, {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
        
        if (!response.ok) {
          console.error('API request failed:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error submitting to API:', error);
      }
    }
    
    // Close popup after 1 second and redirect if URL is provided
    setTimeout(() => {
      handleClosePopup();
      
      // Redirect to redirectUrl if provided
      if (redirectUrl && redirectUrl.trim() !== '') {
        window.location.href = redirectUrl;
      }
    }, 1000);
  };


  return (
    <>
      <Button
        variant={buttonVariant}
        onClick={handleButtonClick}
        sx={{
          textTransform: 'uppercase',
        }}
      >
        {buttonText}
      </Button>

      {isPopupOpen && createPortal(
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
            isolation: 'isolate',
          }}
          onClick={handleOverlayClick}
        >
          <Box
            sx={{
              backgroundColor: styles.popupBg,
              borderRadius: '20px',
              padding: '40px 36px',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 32px 80px rgba(0, 0, 0, 0.12), 0 16px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.05)',
              border: `1px solid ${styles.popupBorder}`,
              position: 'relative',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              background: styles.popupGradient,
            }}
          >
            {/* Enhanced X button */}
            <button
              onClick={handleClosePopup}
              style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                background: 'rgba(255, 255, 255, 0.9)',
                border: `1px solid ${styles.popupBorder}`,
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#333333',
                padding: '0',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = styles.accent;
                e.target.style.backgroundColor = styles.accentRgba;
                e.target.style.transform = 'scale(1.1) rotate(90deg)';
                e.target.style.borderColor = styles.accentRgba3;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#333333';
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                e.target.style.transform = 'scale(1) rotate(0deg)';
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
              }}
            >
              ×
            </button>
            {showConfirmation ? (
              <Box sx={{ textAlign: 'center', padding: '32px 0' }}>
                <Box
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: styles.accentRgba,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    animation: 'pulse 0.6s ease-in-out',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(0.8)', opacity: 0.8 },
                      '50%': { transform: 'scale(1.1)', opacity: 1 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '28px',
                      color: styles.accent,
                      fontWeight: 'bold',
                    }}
                  >
                    ✓
                  </Box>
                </Box>
                <Box
                  as="h5"
                  sx={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#333333',
                    lineHeight: '1.4',
                    letterSpacing: '-0.3px',
                  }}
                >
                  Thank you for subscribing!
                </Box>
                <Box
                  sx={{
                    marginTop: '12px',
                    fontSize: '14px',
                    color: '#333333',
                    opacity: 0.7,
                    fontWeight: '400',
                  }}
                >
                  We'll be in touch soon
                </Box>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Box sx={{ marginBottom: '32px' }}>
                  {/* Email Icon */}
                  <Box
                    sx={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: styles.accentRgba,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        backgroundColor: styles.accentRgba15,
                      },
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ 
                        shapeRendering: 'geometricPrecision',
                        imageRendering: 'crisp-edges'
                      }}
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="14"
                        rx="2"
                        ry="2"
                        fill="none"
                        stroke={styles.accent}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                      <path
                        d="M3 6L12 13L21 6"
                        stroke={styles.accent}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  </Box>
                  <Box
                    as="h5"
                    sx={{
                      margin: 0,
                      marginBottom: popupSubtitle ? '12px' : '48px',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333333',
                      lineHeight: '1.3',
                      letterSpacing: '-0.2px',
                    }}
                  >
                    {popupTitle}
                  </Box>
                  {popupSubtitle && (
                    <Box
                      sx={{
                        margin: 0,
                        marginBottom: '48px',
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#333333',
                        opacity: 0.7,
                        lineHeight: '1.5',
                      }}
                    >
                      {popupSubtitle}
                    </Box>
                  )}
                  <Input
                    type="email"
                    placeholder={placeholderText}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{
                      width: '85%',
                      maxWidth: '320px',
                      margin: '0 auto',
                      display: 'block',
                      padding: '16px 20px',
                      border: '2px solid rgba(0, 0, 0, 0.08)',
                      borderRadius: '16px',
                      fontSize: '15px',
                      backgroundColor: styles.inputBg,
                      color: '#333333',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                      '&:focus': {
                        outline: 'none',
                        borderColor: styles.accent,
                        boxShadow: `0 0 0 4px ${styles.accentRgba15}, 0 8px 25px ${styles.accentRgba15}`,
                        transform: 'translateY(-2px)',
                        backgroundColor: '#ffffff',
                      },
                      '&:hover': {
                        borderColor: styles.accentRgba3,
                        transform: 'translateY(-1px)',
                      },
                      '&::placeholder': {
                        color: styles.placeholder,
                        fontSize: '15px',
                        fontWeight: '400',
                      },
                    }}
                  />
                </Box>
                <Flex sx={{ justifyContent: 'center' }}>
                  <Button
                    type="submit"
                    variant="primary"
                    sx={{
                      textTransform: 'uppercase',
                    }}
                  >
                    {popupButtonText}
                  </Button>
                </Flex>
              </form>
            )}
          </Box>
        </Box>,
        document.body
      )}
    </>
  );
};

export default EmailSignup;
